import React from 'react'
import { useWizard } from '@/components/common/wizard/WizardContext'
import { formatCurrencyAutoLocale } from '@/utils/tools'
import {
    CAMPAIGN_BUDGET_LABEL,
    CAMPAIGN_BUDGET_SPLIT_NOTE,
    isPlainCampaignBudget,
    limitToMinor,
} from '@/utils/campaign-budget'

interface WizardContextType {
    getStepContentData: (stepName: string) => any
}

interface CampaignBudgetReviewProps {
    currency?: string
}

/** The budget mode of a campaign with one campaign budget and optional spend limits, on the review step. */
const CampaignBudgetReview: React.FC<CampaignBudgetReviewProps> = ({ currency }) => {
    const { getStepContentData } = useWizard() as WizardContextType
    const budgetData = getStepContentData('budgetSchedule')
    if (!isPlainCampaignBudget(budgetData)) return null

    const minor = limitToMinor(budgetData.campaignBudget).minor || 0
    const limited = Object.values<{ min?: string; max?: string }>(budgetData.adSetLimits || {}).filter(
        (entry) => limitToMinor(entry?.min).state === 'set' || limitToMinor(entry?.max).state === 'set',
    ).length

    return (
        <div
            className="tw:mx-6 tw:mt-6 tw:flex tw:flex-col tw:gap-2 tw:rounded-lg tw:border tw:p-4"
            data-testid="campaign-budget-review"
        >
            <span className="tw:text-sm tw:font-semibold">{CAMPAIGN_BUDGET_LABEL}</span>
            <p className="tw:text-sm">
                {`${formatCurrencyAutoLocale({
                    amount: minor / 100,
                    currency,
                })} a day for the whole campaign, shared by Meta across the audiences.`}
            </p>
            <p className="tw:text-sm">
                {limited > 0
                    ? `${limited} ${limited === 1 ? 'audience has' : 'audiences have'} a minimum or maximum daily spend.`
                    : 'No minimum or maximum daily spend is set for the audiences.'}
            </p>
            <p className="tw:text-xs tw:text-gray-200">{CAMPAIGN_BUDGET_SPLIT_NOTE}</p>
        </div>
    )
}

export default CampaignBudgetReview
